import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RestService } from '../services/rest.service';
import { TranslationService } from '../services/translation.service';
import { LoginService } from '../services/login.service';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth.service';
import { HandoffService } from '../services/handoff.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import { formatFirstNameForDisplay } from '../shared/name-display';
import { shouldGateCaptureToPhone } from '../shared/device';
import { PhoneHandoffComponent } from './phone-handoff/phone-handoff.component';
import { PageBackService } from '../services/page-back.service';

interface TestStep {
  id: string;
  number: number;
  title: string;
  description: string;
  isActive: boolean;
  isPassed: boolean;
  route?: string;
}

@Component({
  selector: 'app-new-test',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, PhoneHandoffComponent],
  templateUrl: './new-test.component.html',
  styleUrls: ['./new-test.component.scss'],
})
export class NewTestComponent implements OnInit, OnDestroy {
  scanId: string;
  subscription: Subscription = new Subscription();
  title = '';
  subtitle = '';
  medicalDisclaimer = '';
  skipDevText = '';
  backLabel = '';
  testSteps: TestStep[] = [];
  // Face-scan capture is phone-only: desktop/tablet taps of step 1 show the QR
  // handoff instead of opening the camera (DEV_SPEC_desktop_capture_warning.md).
  showPhoneHandoff = false;
  // Phone side of a cross-device handoff: while redeeming the token + signing in.
  resuming = false;
  // Admin "skip test" in flight (guards double-taps while the real scan is
  // created + the skipped part is seeded server-side).
  skipping = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private restService: RestService,
    private translationService: TranslationService,
    private loginService: LoginService,
    private sessionService: SessionService,
    private authService: AuthService,
    private handoffService: HandoffService,
    private reporter: ErrorReportingService,
    private location: Location,
    private pageBack: PageBackService
  ) {
    this.initializeTranslations();
    this.subscribeToLanguageChanges();

    this.subscription.add(
      this.route.queryParams.pipe().subscribe((params) => {
        // Phone landed from a desktop/tablet QR (or SMS): redeem the one-time
        // token, establish the SAME session with no re-login, resume at capture.
        if (params['handoff'] && !this.resuming) {
          this.resumeFromHandoff(params['handoff']);
          return;
        }
        if (params['scanId']) {
          this.scanId = params['scanId'];

          this.testSteps[0].isPassed = true;
          this.testSteps[0].isActive = false;

          this.setRoutes();
          this.checkWhatDataIsReady();
        }
      })
    );
  }

  private lang(): string {
    return this.translationService.getCurrentLanguage() || 'he';
  }

  /** Phone resume: token -> Supabase magiclink token_hash -> session -> capture. */
  private async resumeFromHandoff(token: string): Promise<void> {
    this.resuming = true;
    // Was the phone already signed into an app session before this handoff?
    const alreadyLoggedIn = !!this.sessionService.session?.token;
    try {
      const res = await this.handoffService.redeem(token);
      if (res?.tokenHash) {
        // Fresh phone: the loading spinner consumes this stored redirect and
        // lands her in the capture step once the app session is established
        // (guarantees the session exists before the camera opens).
        if (!alreadyLoggedIn) {
          this.sessionService.setRedirectUrl(`/${this.lang()}/app/test`);
        }
        const { error } = await this.authService.verifyOtp(
          res.tokenHash,
          'email'
        );
        if (error) throw error;
        // Already-logged-in (same user): the session emit won't re-fire the
        // post-login router, so navigate straight to the capture step.
        if (alreadyLoggedIn) {
          this.router.navigate([`/${this.lang()}/app/test`]);
        }
        return; // else: session emits -> /app/loading spinner -> /app/test
      }
      // Invalid / used / expired -> never a dead end: send her to login.
      this.router.navigate([`/${this.lang()}/login`]);
    } catch (e) {
      this.reporter.report(e, 'handoff-resume');
      this.router.navigate([`/${this.lang()}/login`]);
    } finally {
      this.resuming = false;
    }
  }

  /**
   * The screen opens at its top. It used to nudge the last step card into view
   * on mobile (item 17, so the fourth test is not missed), but that scrolled the
   * header 99px out of the viewport, and the header is where this screen's only
   * exit now lives (F2 / B2 moved it off the bottom nav). She arrived on a
   * screen with no visible way back.
   *
   * Item 17 is not lost: the subtitle she lands on already says the check-in is
   * four short steps, which is the same fact the nudge was there to convey, and
   * it is readable without moving the page at all.
   */

  private initializeTranslations(): void {
    this.updateTranslations();
  }

  private subscribeToLanguageChanges(): void {
    this.translationService.currentLanguage$.subscribe(() => {
      this.updateTranslations();
    });
  }

  private updateTranslations(): void {
    const name = formatFirstNameForDisplay(
      this.sessionService.session?.user?.fname,
    );
    this.title = name
      ? this.translationService.translate('newTest.title', { name })
      : this.translationService.translate('newTest.titleNoName');
    this.subtitle = this.translationService.translate('newTest.subtitle');
    this.medicalDisclaimer = this.translationService.translate('newTest.medicalDisclaimer');
    this.skipDevText = this.translationService.translate('miscUi.skipDev');
    this.backLabel = this.translationService.translate('shell.nav.back');

    const currentState = this.testSteps.reduce((acc, step) => {
      acc[step.id] = {
        isActive: step.isActive,
        isPassed: step.isPassed,
        route: step.route
      };
      return acc;
    }, {} as Record<string, { isActive: boolean; isPassed: boolean; route?: string }>);

    this.testSteps = [
      {
        id: 'selfie',
        number: 1,
        title: this.translationService.translate('newTest.steps.faceScan.title'),
        description: this.translationService.translate('newTest.steps.faceScan.description'),
        isActive: currentState['selfie']?.isActive ?? true,
        isPassed: currentState['selfie']?.isPassed ?? false,
        route: currentState['selfie']?.route || '/app/test',
      },
      {
        id: 'symptoms',
        number: 2,
        title: this.translationService.translate('newTest.steps.symptoms.title'),
        description: this.translationService.translate('newTest.steps.symptoms.description'),
        isActive: currentState['symptoms']?.isActive ?? false,
        isPassed: currentState['symptoms']?.isPassed ?? false,
        route: currentState['symptoms']?.route || '',
      },
      {
        id: 'cognitive',
        number: 3,
        title: this.translationService.translate('newTest.steps.sharpScale.title'),
        description: this.translationService.translate('newTest.steps.sharpScale.description'),
        isActive: currentState['cognitive']?.isActive ?? false,
        isPassed: currentState['cognitive']?.isPassed ?? false,
        route: currentState['cognitive']?.route || '',
      },
      {
        id: 'sense',
        number: 4,
        title: this.translationService.translate('newTest.steps.sense.title'),
        description: this.translationService.translate('newTest.steps.sense.description'),
        isActive: currentState['sense']?.isActive ?? false,
        isPassed: currentState['sense']?.isPassed ?? false,
        route: currentState['sense']?.route || '',
      },
    ];
  }

  private setRoutes() {
    this.testSteps[1].route = `/app/symptoms-form/${this.scanId}`;
    this.testSteps[2].route = `/app/cognitive/${this.scanId}`;
    this.testSteps[3].route = `/app/sense/${this.scanId}`;
  }

  async checkWhatDataIsReady() {
    try {
      const response: any = await this.restService.request({
        path: `scan/${this.scanId}/new-test`,
        method: 'GET',
      });
      console.log('NewTestComponent: new test retrieved:', response);
      this.testSteps.forEach((step) => {
        step.isPassed = response.data[step.id];

        if (!response.data['selfie']) {
          this.testSteps[0].isActive = true;
        } else if (!response.data['symptoms']) {
          this.testSteps[1].isActive = true;
        } else if (!response.data['cognitive']) {
          this.testSteps[2].isActive = true;
        } else if (!response.data['sense']) {
          this.testSteps[3].isActive = true;
        } else {
          this.router.navigate(['/app/results', this.scanId]);
        }
      });
      this.revealActiveStep();
    } catch (error) {
      console.error(
        'NewTestComponent: failed to check what data is ready:',
        error
      );
    }
  }

  onStepClick(step: TestStep) {
    if (!step.route) return;
    // Hard gate: the face-scan photo capture (step 1) is phone-only. On a
    // desktop/tablet, tapping it opens the phone-handoff instead of the camera.
    // Only the capture is gated — the other steps navigate normally on any device.
    if (
      step.id === 'selfie' &&
      step.isActive &&
      !step.isPassed &&
      shouldGateCaptureToPhone()
    ) {
      this.showPhoneHandoff = true;
      return;
    }
    console.log('NewTestComponent: navigating to:', step.route);
    this.router.navigate([step.route]);
  }

  closePhoneHandoff() {
    this.showPhoneHandoff = false;
  }

  async onSkipStep(step: TestStep, event: Event) {
    event.stopPropagation();
    if (this.skipping) return;
    this.skipping = true;

    try {
      // Skip must produce a REAL scan, not hijack the shared seeded dev scan.
      // The old code pointed this.scanId at the dev scan (4ce51d0f), which only
      // exists off-prod - so on prod every follow-up call 404'd and the flow
      // dead-ended. Now: create a real scan the first time (owned by the user if
      // logged in), then copy ONLY the skipped step's data from the template.
      // Every non-skipped step stays the user's own real data. (Per Mike.)
      if (!this.scanId) {
        const created: any = await this.restService.request({
          path: 'scan/create',
          method: 'POST',
          body: {},
          throwError: true,
        });
        this.scanId = created?.scanId;
      }
      if (!this.scanId) throw new Error('skip: no scanId after create');

      await this.restService.request({
        path: `scan/${this.scanId}/skip-part`,
        method: 'POST',
        body: { part: step.id },
        throwError: true,
      });

      step.isPassed = true;
      step.isActive = false;
      this.setRoutes();

      if (step.id === 'selfie') {
        this.testSteps[1].isActive = true;
      } else if (step.id === 'symptoms') {
        this.testSteps[2].isActive = true;
      } else if (step.id === 'cognitive') {
        this.testSteps[3].isActive = true;
      } else if (step.id === 'sense') {
        this.router.navigate(['/app/results', this.scanId]);
      }
    } catch (e) {
      this.reporter.report(e, 'skip-step');
    } finally {
      this.skipping = false;
    }
  }

  isAdmin(): boolean {
    return this.loginService.isAdmin();
  }

  canSkipTests(): boolean {
    return this.loginService.canSkipTests();
  }

  // F2 / B2: the single exit for this screen, top-left in its own header.
  // Same destination the bottom nav's Back tab had (history back), so moving it
  // changes where the control sits, not where it leads.
  ngOnInit(): void {
    // B3: this screen now renders its OWN back, so the nav's Back tab steps
    // aside and only one back is on screen. The phone-handoff view that can
    // replace this one carries its own .ph-back, so the claim holds for the
    // whole component lifetime.
    this.pageBack.register();
  }

  // Later steps (e.g. the last one) sit below the fold on a phone: bring the
  // active card into view so she doesn't have to scroll to find it. The header
  // is sticky, so the back button stays on screen.
  private revealActiveStep() {
    const idx = this.testSteps.findIndex((s) => s.isActive);
    if (idx <= 0) return;
    setTimeout(() => {
      const el = document.querySelectorAll('.test-step')[idx] as HTMLElement | undefined;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }

  goBack() {
    this.location.back();
  }

  ngOnDestroy() {
    this.pageBack.release();
    this.subscription.unsubscribe();
  }
}
